'use client';

import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { DataTable } from '@/components/data-table/data-table';
import { useDataTable } from '@/hooks/use-data-table';
import { DataTableSearch } from '@/components/data-table/data-table-search';
import { useOrgNavigate } from '@/hooks/use-app-navigate';

import { getCustomersTableColumns } from './customers-table-columns';
import { CustomersTableActionBar } from './customers-table-action-bar';
import { CustomersTableSkeleton } from './CustomersTableSkeleton';
import { MainTableState } from '@/lib/utils/table-state-utils';
import { Customer } from '@/lib/api/customers';
import type { TableConfig } from '@/types/data-table';
import { DEFAULT_SKELETON_ROWS } from '@/config/data-table';

interface CustomersTableProps {
  customers: Customer[];
  total: number;
  loading: boolean;
  searchQuery?: string;
  onSearchChange?: (value: string) => void;
  onTableStateChange: (state: MainTableState) => void;
  onRefetch: () => void;
  onExportCustomers?: () => void;
  config?: TableConfig;
}

/**
 * CustomersTable component for displaying customers with DataTable functionality
 */
export function CustomersTable({
  customers,
  total,
  loading,
  searchQuery = '',
  onSearchChange,
  onTableStateChange,
  onRefetch,
  onExportCustomers,
  config = {},
}: CustomersTableProps) {
  const { t, i18n } = useTranslation('customers');
  const navigate = useNavigate();
  const { orgRoutes } = useOrgNavigate();

  const handleRowClick = React.useCallback(
    (customer: Customer) => {
      navigate(orgRoutes.CUSTOMER_DETAILS(customer.id));
    },
    [navigate, orgRoutes],
  );

  // Memoized columns to prevent unnecessary re-renders
  const columns = React.useMemo(() => getCustomersTableColumns({ t }), [t, i18n.language]);

  const { table } = useDataTable({
    data: customers,
    columns,
    total,
    config,
    onTableStateChange,
    getRowId: (row: Customer) => row.id,
  });

  return (
    <DataTable
      key={i18n.language}
      table={table}
      actionBar={<CustomersTableActionBar table={table} onExport={onExportCustomers} />}
      isLoading={loading}
      skeletonRows={<CustomersTableSkeleton rows={config.skeletonRows || DEFAULT_SKELETON_ROWS} />}
      onRefresh={onRefetch}
      onExport={onExportCustomers}
      onRowClick={handleRowClick}
      extraFilters={<>
        {onSearchChange && (
          <div className='w-full md:w-[200px]'>
            <DataTableSearch value={searchQuery} onChange={onSearchChange} placeholder="Search anything..." />
          </div>
        )}
      </>}
    >

    </DataTable>
  );
}
